/**
 * Single source of truth for how a patient's linking identifier (MRN, or the
 * last five digits of the SSN) is masked and announced to screen readers.
 * Every surface that renders the identifier must go through these helpers —
 * drift between copies of this logic is a privacy bug.
 */

import { hasSubmittedMedicalRecordNumber } from './identity-match.utils';

/** Structural view of any request/detail model carrying the identifier pair. */
export interface PatientIdentifierSource {
  medicalRecordNumber: string | null;
  lastFiveSocialSecurityNumber?: string | null;
}

/** Options for {@link getVisibleIdentifier} so each layout keeps its format. */
export interface VisibleIdentifierOptions {
  /** Between the `MRN`/`SSN` label and the value. Defaults to `': '`. */
  separator?: string;
  /** Placeholder when neither identifier is present. Defaults to `'—'`. */
  empty?: string;
}

/** Mask an SSN down to its last five digits, e.g. `••••12345`. */
export function maskSsnLastFive(lastFive: string): string {
  return `••••${lastFive}`;
}

/**
 * The MRN to display, or null when none was submitted. Whitespace-only counts as none — the same
 * rule the mismatch check uses (see {@link hasSubmittedMedicalRecordNumber}).
 */
function getSubmittedMrn(source: PatientIdentifierSource): string | null {
  const mrn = source.medicalRecordNumber;
  return mrn !== null && hasSubmittedMedicalRecordNumber(mrn) ? mrn : null;
}

/** Visible identifier text. MRN takes precedence over SSN; SSN is always masked. */
export function getVisibleIdentifier(
  source: PatientIdentifierSource,
  options: VisibleIdentifierOptions = {},
): string {
  const { separator = ': ', empty = '—' } = options;

  const mrn = getSubmittedMrn(source);
  if (mrn) {
    return `MRN${separator}${mrn}`;
  }

  if (source.lastFiveSocialSecurityNumber) {
    return `SSN${separator}${maskSsnLastFive(source.lastFiveSocialSecurityNumber)}`;
  }

  return empty;
}

/** Decomposed identifier for layouts that render the type and value separately. */
export interface IdentifierParts {
  /** Short type label: `'MRN'`, `'SSN (last 5)'`, or `'—'` when absent. */
  typeLabel: string;
  /** Field caption for detail layouts: `'MRN Provided'`, `'SSN (last 5)'`, or `'Identifier'`. */
  fieldLabel: string;
  /** Visible value; the SSN is always masked. `'—'` when absent. */
  value: string;
  /** True when `value` is the masked SSN (gets the spaced-dot styling). */
  isMasked: boolean;
  /** False when neither identifier is present and the parts are placeholders. */
  hasIdentifier: boolean;
}

/**
 * Split the identifier into label/value parts. Same precedence as
 * {@link getVisibleIdentifier} (MRN wins over SSN; SSN is always masked) —
 * kept here so layouts that show the pieces separately can't drift from it.
 */
export function getIdentifierParts(source: PatientIdentifierSource): IdentifierParts {
  const mrn = getSubmittedMrn(source);
  if (mrn) {
    return {
      typeLabel: 'MRN',
      fieldLabel: 'MRN Provided',
      value: mrn,
      isMasked: false,
      hasIdentifier: true,
    };
  }

  if (source.lastFiveSocialSecurityNumber) {
    return {
      typeLabel: 'SSN (last 5)',
      fieldLabel: 'SSN (last 5)',
      value: maskSsnLastFive(source.lastFiveSocialSecurityNumber),
      isMasked: true,
      hasIdentifier: true,
    };
  }

  return {
    typeLabel: '—',
    fieldLabel: 'Identifier',
    value: '—',
    isMasked: false,
    hasIdentifier: false,
  };
}

/** Screen-reader label for the identifier; exposes no more than the visible text. */
export function getIdentifierAriaLabel(source: PatientIdentifierSource): string {
  const mrn = getSubmittedMrn(source);
  if (mrn) {
    return `MRN ${mrn}`;
  }

  if (source.lastFiveSocialSecurityNumber) {
    return `SSN ending in ${source.lastFiveSocialSecurityNumber}`;
  }

  return 'No identifier available';
}
